import * as Sentry from '@sentry/react';

import { AXIOS_NO_RESPONSE_NOISE_MESSAGES } from './util/sentryFilters';

export const SENTRY_DSN_PLACEHOLDER = '###SENTRY_API_KEY_PLACEHOLDER###';
export const SENTRY_RELEASE_PLACEHOLDER = '###SENTRY_RELEASE_PLACEHOLDER###';
export const SENTRY_ENVIRONMENT_PLACEHOLDER =
  '###SENTRY_ENVIRONMENT_PLACEHOLDER###';

const DENY_URLS = ['staticw2.yotpo.com'];
// ResizeObserver: benign UI noise. Axios no-response: see sentryFilters.js.
const IGNORE_ERRORS = ['ResizeObserver', ...AXIOS_NO_RESPONSE_NOISE_MESSAGES];

export function isUsableSentryDsn(dsn) {
  return Boolean(dsn) && dsn !== SENTRY_DSN_PLACEHOLDER;
}

/**
 * Build browser Sentry.init options from HTML-injected window globals.
 * Returns null when DSN is missing or still the Ansible placeholder.
 */
export function getBrowserSentryOptions(globals = window) {
  const dsn = globals && globals.__SENTRY_DSN__;
  if (!isUsableSentryDsn(dsn)) {
    return null;
  }

  const options = {
    dsn,
    denyUrls: DENY_URLS,
    ignoreErrors: IGNORE_ERRORS,
  };

  const release = globals.__SENTRY_RELEASE__;
  if (release && release !== SENTRY_RELEASE_PLACEHOLDER) {
    options.release = release;
  }

  const environment = globals.__SENTRY_ENVIRONMENT__;
  if (environment && environment !== SENTRY_ENVIRONMENT_PLACEHOLDER) {
    options.environment = environment;
  }

  return options;
}

function hasSentryClient() {
  return Boolean(Sentry.getClient && Sentry.getClient());
}

/**
 * BE-aligned triage tags. Host → `site`; pathname only → `page_path`.
 * Full URL is left to the SDK request field (no duplicate tag).
 * Accepts `window.location` or a React Router location (+ hostname).
 */
export function applyBrowserSentryTags(location = window.location) {
  if (!location || !hasSentryClient()) {
    return;
  }
  const hostname =
    location.hostname ||
    (typeof window !== 'undefined' && window.location
      ? window.location.hostname
      : '');
  if (hostname) {
    Sentry.setTag('site', hostname);
  }
  if (typeof location.pathname === 'string') {
    Sentry.setTag('page_path', location.pathname);
  }
}

/**
 * Init client Sentry when a real DSN is present (Approach A: bundled SDK).
 * @returns {boolean} whether Sentry.init was called
 */
export function initBrowserSentry() {
  const options = getBrowserSentryOptions();
  if (!options) {
    return false;
  }
  Sentry.init(options);
  applyBrowserSentryTags();
  return true;
}
